import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import FastlyImage from '@components/common/fastly-image'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { NODE_JS } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['wd'].trialCourseQuery}`

export const NodeJSSeo = formatSeoConfig({
  title: 'Node.js for Kids: Learn Backend Coding Online',
  description:
    'Node.js classes for kids with expert instructors. Build web apps and learn server-side JavaScript with a structured curriculum at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/node-js/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-light-v3.jpg',
})

const BegineerGuide = () => (
  <div className="space-y-3">
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-6.jpg`}
        alt="Node js guides for kids"
        width={512}
        height={341}
      />
    </div>
    <h3 className="font-600 text-lg lg:text-xl">
      Introduction: What is Node.js and Why Should Kids Learn It?
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-7.jpg`}
        alt="Kids learning node.js"
        width={512}
        height={341}
      />
    </div>
    <p>
      Node.js is a powerful tool that allows developers to use JavaScript for
      backend development, enabling them to build scalable applications, manage
      server-side logic, and work with databases. Traditionally, JavaScript has
      been used for front-end development (the part of websites users interact
      with), but Node.js lets kids and teens expand their coding skills to
      include back-end coding. If your child has mastered basic coding languages
      like Scratch or Python, learning Node.js is an excellent way to take their
      programming skills to the next level.
    </p>
    <h4 className="font-600 lg:text-lg">Why Node.js for Kids and Teens?</h4>
    <p>
      Node.js is an ideal tool for kids learning back-end coding because it
      makes server-side development approachable and fun. Node.js for kids
      courses introduce children to key programming concepts in a hands-on,
      engaging way. It is beginner-friendly, and since Node.js uses JavaScript,
      it builds upon the skills your child may have already developed with
      front-end coding.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      The Benefits of Learning Node.js for Kids: Who Uses Node.js? Real
      Companies, Real Impact
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-8.jpg`}
        alt="Node.js used by different companies"
        width={512}
        height={341}
      />
    </div>
    <p>
      Many leading companies around the world use Node.js in their technology
      stacks. It&lsquo;s a popular choice for backend services, APIs, and
      real-time applications due to its speed, scalability, and performance.
      Here are some notable examples:
    </p>
    <ul>
      <li>
        <strong>Netflix</strong> uses Node.js for backend services to improve
        startup times and streamline content delivery.
      </li>
      <li>
        <strong>PayPal</strong> adopted Node.js to boost performance and reduce
        response times across its web applications and APIs.
      </li>
      <li>
        <strong>LinkedIn</strong> moved parts of its backend to Node.js,
        significantly improving the performance of its mobile services.
      </li>
      <li>
        <strong>Uber</strong> relies on Node.js for its real-time matching
        system, efficiently handling massive volumes of ride requests.
      </li>
      <li>
        <strong>Walmart</strong> utilizes Node.js for its high-traffic
        e-commerce platform, especially during peak events like Black Friday.
      </li>
      <li>
        <strong>Trello</strong> uses Node.js on the backend to support real-time
        updates in its project management application.
      </li>
      <li>
        <strong>NASA</strong> employs Node.js to manage data security and
        internal systems related to astronaut information.
      </li>
      <li>
        <strong>eBay</strong> implemented Node.js for backend services that
        require real-time updates, such as auctions and transactions.
      </li>
      <li>
        <strong>Medium</strong> leverages Node.js for server-side rendering and
        efficient content delivery.
      </li>
      <li>
        <strong>Mozilla</strong> incorporates Node.js in its development tools
        and infrastructure.
      </li>
      <li>
        <strong>GoDaddy</strong> uses Node.js for backend services that power
        its web hosting and domain registration platform.
      </li>
    </ul>
    <h4 className="font-600 lg:text-lg">
      Real-World Project Teens can Build with Node.js
    </h4>
    <p>
      Node.js provides a seamless path to full-stack development for teens. Your
      child can learn how to build both the front-end and back-end of websites
      and apps. Kids don’t just learn theory but also build real, practical
      projects! With Node.js, your child will work on hands-on applications
      like:
    </p>
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-9.jpg`}
        alt="Realtime Node.js projects for kids"
        width={512}
        height={341}
      />
    </div>
    <ol>
      <li>
        <strong>Creating a Basic Web Server:</strong> Learn how websites
        actually load content from servers
      </li>
      <li>
        <strong>Building a Weather App:</strong> Fetch real-time weather data
        using public APIs
      </li>
      <li>
        <strong>Making a URL Shortener:</strong> Just like Bitly, create a
        service that turns long links into short ones
      </li>
      <li>
        <strong>Developing a Chat App:</strong> Build a real-time messaging
        platform like WhatsApp (simplified version)
      </li>
      <li>
        <strong>Designing a Simple Online Store:</strong> Add products, a
        shopping cart, and checkout (like a mini Amazon!)
      </li>
    </ol>
    <p>
      These projects give kids real exposure to how modern websites and apps are
      built – all while having fun and thinking creatively!
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      What Will My Child Learn in a Node.js for Kids Course?
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-10.jpg`}
        alt="Node.js course content"
        width={512}
        height={341}
      />
    </div>
    <p>
      In a Node.js for kids course, students will start with foundational
      concepts like understanding how Node.js works and its key features. The
      course is designed to gradually introduce more advanced topics.
      Here&lsquo;s what your child will learn:
    </p>
    <ol>
      <li>
        <strong>Node.js Basics:</strong> Your child will start by understanding
        the core concepts of Node.js, such as how it handles multiple requests
        simultaneously and why it&lsquo;s a good choice for backend development.
      </li>
      <li>
        <strong>Server-Side Coding:</strong> Students will learn how to write
        code that runs on a server, interacting with the web browser to process
        requests and serve dynamic content.
      </li>
      <li>
        <strong>Using the File System (fs):</strong> One of the first tasks in
        learning Node.js is to work with the fs (File System) module, which
        allows kids to create, read, update, and delete files on the server.
      </li>
      <li>
        <strong>Databases and SQL:</strong> Your child will get hands-on
        experience working with databases, learning how to write basic SQL
        queries like INSERT, SELECT, UPDATE, and JOIN.
      </li>
      <li>
        <strong>Build a Web Server:</strong> Students will create their first
        web server using Node.js to respond to requests, display content, and
        interact with users.
      </li>
      <li>
        <strong>Advanced Node.js Projects:</strong> As they progress, students
        can build more complex applications such as APIs and chatbots, allowing
        them to apply backend coding in real-time projects.
      </li>
    </ol>
    <h3 className="font-600 text-lg lg:text-xl">
      What to Learn After JavaScript for Kids?
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-11.jpg`}
        alt="Front end vs backend"
        width={512}
        height={341}
      />
    </div>
    <p>
      After your child has mastered JavaScript basics, Node.js for kids offers
      the perfect next step. Kids who are comfortable with basic programming
      concepts will find Node.js an exciting challenge, bridging the gap between
      front-end and back-end development. As a follow-up, Express.js tutorials
      for kids are an excellent way to take Node.js knowledge to the next level,
      allowing students to build more advanced web applications.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      How Can Node.js for Teens Set Up for Future Careers?
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-12.jpg`}
        alt="Future tech jobs for teens"
        width={512}
        height={341}
      />
    </div>
    <p>
      Learning Node.js for teens is not only about coding for fun but it&lsquo;s
      also about preparing for the future. As backend development becomes a more
      integral part of the tech industry, Node.js is one of the most valuable
      skills your child can acquire. It sets the foundation for careers in
      software development, full-stack development, AI programming, and more. By
      learning server-side coding early on, your child will have a competitive
      edge for tech jobs in the future.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      Simple Node.js Projects for Kids to Build
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-13.jpg`}
        alt="Simple Node.js projects for kids"
        width={512}
        height={341}
      />
    </div>
    <p>
      With Node.js for kids, kids and teens can work on simple yet impactful
      projects. Here are a few project ideas to kick-start their journey:
    </p>
    <ol>
      <li>
        <strong>YouTube Video Tracker</strong>: Build a mini server that lets
        users save and organize favorite video links (great for managing YouTube
        playlists).
      </li>
      <li>
        <strong>Mini Discord Clone</strong>: Create a basic real-time chat app
        with usernames and channels, similar to Discord or Slack.
      </li>
      <li>
        <strong>Spotify Song Search API</strong>: Use an open music API to
        search for songs, albums, or artists and display results in a fun
        interface.
      </li>
      <li>
        <strong>AI Art Gallery API</strong>: Connect to an AI image API (like
        DALL·E) and build a simple app to fetch and display generated artwork.
      </li>
      <li>
        <strong>File Organizer Bot</strong>: Make a file manager that
        automatically sorts files into folders (like images, docs, etc.) on your
        computer/server.
      </li>
      <li>
        <strong>Online Poll Maker</strong>: Let users create and vote on polls
        in real time, similar to Instagram or YouTube polls.
      </li>
    </ol>
    <h3 className="font-600 text-lg lg:text-xl">
      What Should Teens Learn After Node.js?
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/nodejs/nodejs-14.jpg`}
        alt="What should teen learn after node.js?"
        width={512}
        height={341}
      />
    </div>
    <p>
      After your teen learns Node.js, they can continue their coding journey by:
    </p>
    <ul>
      <li>
        Diving into Full-stack Development by learning React.js or Angular for
        front-end development.
      </li>
      <li>
        Exploring Database management using MongoDB or MySQL to connect their
        applications to databases.
      </li>
      <li>
        Learning API development in depth and exploring frameworks like
        Express.js for creating robust backend applications.
      </li>
    </ul>
  </div>
)
const NodeJS = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...NodeJSSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[NODE_JS].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn Node.js with the Top AI & coding Course Instructors for Kids and Teens"
        description="Introduce Your Child to the World of Server-Side Programming with Node.js. At Codingal, we make backend development engaging and accessible for young learners. Our Node.js for Kids module provides an interactive introduction to server-side programming, guiding students through the process of building scalable applications. Using Node.js, a powerful JavaScript runtime, kids will learn to create dynamic websites that go beyond static pages, making their coding experience both fun and impactful."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={NODE_JS}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={NODE_JS} />
          <CourseFeatures
            courseTitle={NODE_JS}
            title={
              <Trans>An engaging Node.js programming lessons for kids</Trans>
            }
          />
          <ClassCurriculum
            courseName={NODE_JS}
            title="Our Node.js Class Curriculum"
            description={
              <>
                <p>
                  The Node.js curriculum at Codingal builds a strong foundation
                  in backend development. Students begin by learning what
                  Node.js is – a tool that lets you run JavaScript on the server
                  to build websites and apps quickly and efficiently.
                </p>
                <p>
                  They explore how Node.js works differently from other web
                  servers, how it can handle many requests at the same time
                  without slowing down, and its practical uses like creating,
                  reading, writing, and managing files on a server using simple
                  commands.
                </p>
                <p>
                  Students gain hands-on experience with:
                  <ul>
                    <li>
                      Using built-in tools like ‘fs’ (File System module), which
                      is a feature in Node.js that helps you work with files
                      easily – for example, creating new files, reading
                      information from files, updating file content, or deleting
                      files when needed.
                    </li>
                    <li>
                      Understanding databases, which are like organised digital
                      cupboards where information is stored in tables with rows
                      and columns, similar to a spreadsheet.
                    </li>
                    <li>
                      Practicing simple database commands to add new information
                      into tables (called INSERT queries) and look up
                      information (called SELECT queries) when they need it.
                    </li>
                    <li>
                      They move on to learning how to change existing
                      information (using UPDATE queries) and how to connect
                      information from different tables (called JOIN queries),
                      helping them build apps that can handle more complex data
                      tasks.
                    </li>
                  </ul>
                </p>
                <p>
                  The curriculum ensures students apply these concepts through
                  projects, so by the end, they can build real backend systems
                  that work with files and databases, learning each topic step
                  by step with confidence.
                </p>
                <div>
                  <FastlyImage
                    src={`/images/courses/nodejs/nodejs-1.jpg`}
                    alt="Alt Tags : Node.js projects for teen"
                    width={512}
                    height={341}
                  />
                </div>
                <p>
                  Key areas covered include:
                  <ol>
                    <li>
                      <b>File Management</b>: Using Node.js&lsquo;s built-in fs
                      module to work with files, such as creating, reading, and
                      updating file content.
                    </li>
                    <li>
                      <b>Database Basics</b>: Learning the concepts of
                      databases, comparing them to organized digital cupboards
                      with tables and rows, and practicing database commands
                      such as INSERT (adding data), SELECT (retrieving data),
                      and UPDATE (modifying data).
                    </li>
                    <li>
                      <b>Backend Operations</b>: Gaining hands-on experience
                      with basic backend tasks like connecting information from
                      multiple tables, querying databases, and understanding how
                      data management works on the server side.
                    </li>
                  </ol>
                </p>
                <p>
                  By the end of the course, students will have the skills to
                  build backend systems for websites and apps using Node.js, and
                  they will be well-prepared to dive deeper into full-stack
                  development.
                </p>
              </>
            }
          />
          <Activities courseName={NODE_JS} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & Web
                Development coding tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  undergo rigorous training before they teach, which is
                  reflected in our exceptional Net Promoter Score (NPS) of 86.
                  Students and parents consistently rate our teachers 4.9 out of
                  5, while our curriculum earns a strong 4.8 out of 5. We’re
                  proud to have a faculty that’s 91% female and 9% male,
                  creating an inclusive and encouraging learning environment.
                  Each tutor is trained to understand individual student needs
                  and deliver personalized support, even in group settings.
                  Additionally, students have 24/7 access to expert tutors for
                  instant doubt resolution whenever they need help.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Node.Js
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  Codingal offers a nurturing environment where young learners
                  can explore backend development using Node.js without fear.
                  Our structured curriculum introduces what Node.js is, how it
                  works on the server, and how it can create powerful backend
                  systems. With supportive instructors, students build a strong
                  foundation in file management and databases, preparing them
                  for future learning in web development, AI, and coding.
                </span>
              </Trans>
            }
            courseName={NODE_JS}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters Node.js as part of our
                  specialised web development course, delivered through a
                  STEM.org–accredited program built on proven educational
                  frameworks like Bloom’s Taxonomy, BIDE, and STEAM. Our
                  curriculum aligns with Common Core, state standards, AP
                  Computer Science goals, and the K–12 Computer Science
                  Framework, ensuring learning that’s both age-appropriate and
                  college-ready. Through hands-on, project-based learning,
                  students build real-world projects.Backed by STEM.org’s
                  rigorous audits, every Node.js module lays a strong,
                  future-proof foundation in STEM and backend development.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your Node.Js certifications"
            description={
              <>
                In Codingal’s Web Development track, students in grades 6–12
                progress through specialised modules, including Node.js, where
                they advance from learning core backend concepts to building
                real-world applications. Students turn lessons into polished
                backend services and dynamic web applications, gaining hands-on
                experience with industry-standard tools and practices. Upon
                successful completion, they earn the STEM.org-certified Coding
                Grandmaster Certificate; a valuable credential for academic
                portfolios, résumés, and college applications. With
                industry-relevant backend development skills and a recognised
                certificate, learners graduate with confidence, a strong project
                portfolio, and a competitive edge for future studies and
                opportunities ahead.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our Node.js classes are appropriate for middle school & high school students"
            description={
              <>
                At Codingal, our Node.js curriculum takes middle and high school
                students from their very first “Hello, world!” program to build
                real backend applications. We begin with beginner-friendly
                activities that introduce what Node.js is, how it works, and how
                to use built-in tools like the File System (fs) module to
                create, read, and update files easily. As students progress,
                they explore practical database concepts, learning how data is
                stored in tables, how to add and retrieve data using INSERT and
                SELECT queries, and how to update and combine data from
                different tables with UPDATE and JOIN queries. Each topic is
                taught step by step with simple explanations, helping students
                build a strong foundation in backend development and gain the
                confidence to apply these skills in real projects
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'aiepc']} />
          <ContentCard
            urlQuery={urlQuery}
            title={
              <>
                The Ultimate Guide to Node.js for Kids: Learn Server-Side
                Coding, Build Real Projects, and Prepare for Future Tech Careers
              </>
            }
            description={<BegineerGuide />}
          />
          <Faq
            courseName={NODE_JS}
            title="Frequently Asked Questions (FAQs) About Node.js for Kids"
          />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            title="Latest from Our Blog"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={NODE_JS}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Node.js for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default NodeJS
